import { useNavigation } from "@react-navigation/native";
import {
  Booking,
  useBookingConfirm,
  useBookingStart,
  useBookingArrive,
  useBookingComplete,
} from "../services";
import { Button, Row } from "../lib";
import { useCallback } from "react";
import { createStyles, sizes } from "../theme";

type Props = {
  booking: Booking;
};

type Action = {
  label: string;
  onPress: () => void;
};

export function BookingAction({ booking }: Props) {
  // Vars
  const navigation = useNavigation();

  // Handlers
  const [confirmBooking, isConfirming] = useBookingConfirm({ id: booking.id });
  const [startBooking, isStarting] = useBookingStart({ id: booking.id });
  const [arriveBooking, isArriving] = useBookingArrive({ id: booking.id });
  const [completeBooking, isCompleting] = useBookingComplete({
    id: booking.id,
  });

  const isLoading = isConfirming || isStarting || isArriving || isCompleting;

  const handleAccept = useCallback(() => {
    confirmBooking({ id: booking.id });
  }, [confirmBooking, booking.id]);

  const handleComplete = useCallback(() => {
    completeBooking({ id: booking.id });
  }, [completeBooking, booking.id]);

  const handleStart = useCallback(() => {
    startBooking({ id: booking.id });
  }, [startBooking, booking.id]);

  const handleArrive = useCallback(() => {
    arriveBooking({ id: booking.id });
  }, [arriveBooking, booking.id]);

  const handleReview = useCallback(() => {
    navigation.navigate("BookingReview", { id: booking.id });
  }, [navigation, booking.id]);

  const handleReport = useCallback(() => {
    navigation.navigate("BookingReport", { id: booking.id });
  }, [navigation, booking.id]);

  const handleCancel = useCallback(() => {
    navigation.navigate("BookingCancel", { id: booking.id });
  }, [navigation, booking.id]);

  let action: Action | null = null;

  // Verified
  if (booking.verified) {
    action = {
      label: "Accept",
      onPress: handleAccept,
    };
  }

  // Confirmed
  if (booking.confirmed) {
    if (booking.incall) {
      action = {
        label: "Complete",
        onPress: handleComplete,
      };
    } else {
      action = {
        label: "On my way",
        onPress: handleStart,
      };
    }
  }

  // Started
  if (booking.started) {
    action = {
      label: "Arrived",
      onPress: handleArrive,
    };
  }

  // Arrived
  if (booking.arrived) {
    action = {
      label: "Complete",
      onPress: handleComplete,
    };
  }

  // Completed
  if (booking.completed || booking.cancelled) {
    const isCreatable = !!booking.reviewable && !booking.review;
    if (isCreatable) {
      action = {
        label: "Rate client",
        onPress: handleReview,
      };
    }

    const isUpdatable = !!booking.review?.editable;
    if (isUpdatable) {
      action = {
        label: "Edit review",
        onPress: handleReview,
      };
    }
  }

  return (
    <Row.LR>
      <Button compact onPress={handleReport} disabled={booking.reported}>
        {booking.reported ? "Reported" : "Ban client"}
      </Button>
      <Row.R style={$.buttons}>
        {((!booking.cancelled && !booking.completed) || booking.verified) && (
          <Button compact onPress={handleCancel}>
            {booking.verified ? "Decline" : "Cancel"}
          </Button>
        )}
        {!!action && (
          <Button compact em onPress={action.onPress} loading={isLoading}>
            {action.label}
          </Button>
        )}
      </Row.R>
    </Row.LR>
  );
}

const $ = createStyles({
  buttons: {
    gap: sizes["2"],
  },
});
